import React, { createContext, useContext } from 'react';
import { useAccessRestriction } from '@/hooks/useAccessRestriction';
import { useModuleAccess } from '@/hooks/useModuleAccess';

interface AccessRestrictionContextValue {
  isRestricted: boolean;
  // false → the employee's seat/subscription is inactive (server-authoritative:
  // no active seat, CANCELLED, or PAST_DUE that never activated). Distinct from
  // isRestricted, which is derived from seatStatus alone and lags subscription.status
  // during the PAST_DUE/CANCELLED window before the billing cron revokes the seat.
  subscriptionActive: boolean;
  moduleAccess: (key: string) => boolean;
  moduleAccessLoading: boolean;
  // true only once the server's module map has actually arrived. The query is disabled
  // for SuperAdmin/company users and still in flight on first render — in both cases
  // moduleAccess() answers false for every key, which must not be read as "not in plan".
  moduleAccessKnown: boolean;
}

export const AccessRestrictionContext = createContext<AccessRestrictionContextValue>({
  isRestricted: false,
  subscriptionActive: true,
  moduleAccess: () => false,
  moduleAccessLoading: false,
  moduleAccessKnown: false,
});

export const AccessRestrictionProvider = ({ children }: { children: React.ReactNode }) => {
  const { isRestricted } = useAccessRestriction();
  const { data, isLoading: moduleAccessLoading } = useModuleAccess();

  const subscriptionActive = data?.subscriptionActive ?? true;
  const moduleMap = data?.modules ?? {};
  const moduleAccess = (key: string): boolean => moduleMap[key] ?? false;
  const moduleAccessKnown = data !== undefined;

  return (
    <AccessRestrictionContext.Provider
      value={{ isRestricted, subscriptionActive, moduleAccess, moduleAccessLoading, moduleAccessKnown }}
    >
      {children}
    </AccessRestrictionContext.Provider>
  );
};

export const useAccessRestrictionContext = () => useContext(AccessRestrictionContext);
